import {
  Box,
  Button,
  DismissIcon,
  Hide,
  Image,
  Typography,
  useMobileMediaQuery,
} from '@iheartjane/reefer';
import { trackError } from '@iheartjane/tracking';
import { useCallback, useRef } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';

import { useCheckout } from '@jane/shared-ecomm/providers';
import type { Identification } from '@jane/shared/types';
import { ImagePreview, convertIfPDF } from '@jane/shared/util';

import styles from './FormImageUpload.module.css';
import { ALLOWED_FILE_TYPES } from './allowedFileTypes';
import { useUppy } from './useUppy';

export const FormImageUpload = ({
  defaultValue,
  fileName,
  label,
  name,
}: {
  defaultValue?: string;
  fileName: keyof Identification;
  label: string;
  name: `customerInformation.${string}`;
}) => {
  const { setValue } = useFormContext();
  const value = useWatch({ name, defaultValue });

  const isMobile = useMobileMediaQuery({ size: 'lg', width: 'max' });
  const {
    customer: { authenticated },
    dispatches: { updateIdentification },
  } = useCheckout();

  const onFileChange = useCallback(
    (url: string) => {
      setValue(name, url, { shouldValidate: true });
    },
    [name, setValue]
  );

  const {
    changeFile,
    error: uppyError,
    removeFile,
    status,
    thumbnail,
  } = useUppy({
    image: defaultValue,
    name,
    onChange: onFileChange,
  });

  const hiddenInput = useRef<HTMLInputElement>(null);

  const handleAddFile = async (file?: File) => {
    // Upload file to S3 via Uppy and store the S3 URL in the form.
    // This ensures checkout always receives an S3 URL, not base64.
    await changeFile(file);

    // For authenticated users, also sync to their profile (fire-and-forget).
    // This keeps their saved ID up to date without blocking checkout.
    if (authenticated && file) {
      ImagePreview.parse(file).then((result) => {
        convertIfPDF(result.url).then(
          (converted) => {
            updateIdentification({ document: fileName, file: converted });
          },
          (err) => {
            trackError(err, { context: 'profile sync failed' });
          }
        );
      });
    }
  };

  const handleRemoveFile = () => {
    // The html input maintains the previous file even though we clear it from react hook form,
    // which prevents us from uploading the same file again, as it assumes it already has it.
    if (hiddenInput?.current) {
      hiddenInput.current.value = '';
    }

    removeFile();

    // For authenticated users, also clear from their profile
    if (authenticated) {
      updateIdentification({ document: fileName, file: null });
    }
  };

  const imgSrc = thumbnail || value;

  return (
    <Controller
      defaultValue={defaultValue}
      rules={{
        required: true,
      }}
      name={name}
      render={() => {
        return (
          <>
            <Hide isHidden hideFromScreenReader>
              <input
                type="file"
                ref={hiddenInput}
                onChange={(e) => handleAddFile(e.target.files?.[0])}
                accept={ALLOWED_FILE_TYPES.join(',')}
              />
            </Hide>
            {!imgSrc && (
              <Button
                variant="secondary"
                label={`Upload ${label}`}
                loading={status === 'inProgress'}
                onClick={() => hiddenInput.current?.click()}
              />
            )}
            {imgSrc && (
              <Box
                position="relative"
                border="grays-light"
                borderRadius="lg"
                width={isMobile ? '145px' : '185px'}
              >
                <Image
                  borderRadius="rounded"
                  height={isMobile ? '100px' : '125px'}
                  objectFit="cover"
                  src={imgSrc}
                  altText={`${label} preview`}
                />

                <Box top={-8} right={-8} position="absolute">
                  <Button.Icon
                    className={styles['borderedButton']}
                    variant="minimal-inverse"
                    icon={
                      <DismissIcon
                        size="sm"
                        aria-label={`Remove ${label} image`}
                      />
                    }
                    onClick={handleRemoveFile}
                  />
                </Box>
              </Box>
            )}
            {uppyError && (
              <Typography color="error" mt={16} role="alert">
                {uppyError}
              </Typography>
            )}
          </>
        );
      }}
    />
  );
};
